'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { pushClickMaterialGtm, GTMMaterialType } from '@/fo/api/gtm';
import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import PlayIcon from '@/fo/icons/base/icons/play.svg';
import StarIcon from '@/fo/icons/base/icons/star24.svg';
import { UiAdvertisingInformerBase } from '@/fo/ui/advertising/informer/base';
import { UiKitLabelBase, UiKitLabelBaseProps } from '@/fo/ui/kit/label/base';
import { utilGetLinkRel } from '@/fo/util/get-link-rel';

import { styles } from './UiArticleTileBase.styles';

export type Article = {
  id: number;
  title: string;
  tag?: {
    title: string;
    color: string;
    href?: string;
  };
  description?: string;
  href: string;
  date?: string;
  isoDate?: string;
  readTime?: string;
  image: string;
  withoutButton?: boolean;
  rating?: number;
  avatar?: string;
  doctorAvatar?: string;
  /** @deprecated Использовать label */
  subtitle?: string;
  label?: (UiKitLabelBaseProps & { id: string; title?: string })[];
  target?: boolean;
  withDoctor?: boolean;
  bestRating?: number;
  address?: string;
  erid?: string;
  /** Тип материала для GTM click_material */
  materialType?: GTMMaterialType;
};

export interface UiArticleTileBaseProps {
  article: Article;
  withoutButton?: boolean;
  className?: string;
  isVideo?: boolean;
  withMicrodata?: boolean;
  advertisingWarningText?: string;
  sendMultiple?: boolean;
}

export function UiArticleTileBase({
  className,
  article,
  withoutButton = false,
  isVideo = false,
  withMicrodata = false,
  advertisingWarningText,
  sendMultiple = false
}: UiArticleTileBaseProps) {
  const slots = styles({
    withDoctor: !!article.withDoctor,
    hasDate: Boolean(article.date || article.readTime)
  });

  const t = useTranslations();

  return (
    <div className={slots.base()}>
      <div
        className={slots.item({ className })}
        {...(withMicrodata && {
          itemScope: true,
          itemType: 'https://schema.org/Article'
        })}
      >
        <Link
          href={article.address ? `/${article.address}` : article.href}
          className={slots.articleLink()}
          target={article.target ? '_blank' : '_self'}
          rel={utilGetLinkRel(article.href, article.target)}
          onClick={() => {
            if (!article.materialType) return;

            pushClickMaterialGtm({
              eventCategory:
                article.label?.[0]?.title || article.tag?.title || '',
              moduleType: article.materialType,
              moduleTitle: article.title
            });
          }}
        >
          {article.title}
        </Link>
        <div className={slots.imageWrap()}>
          <div className={slots.metaList()}>
            {Boolean(article.date || article.readTime) && (
              <div className={slots.metaItem()}>
                {article.readTime && <div>{article.readTime}</div>}
                {Boolean(article.date && article.readTime) && <div>/</div>}
                {article.date && (
                  <div {...(withMicrodata && { itemProp: 'datePublished' })}>
                    {article.isoDate && (
                      <meta
                        itemProp="datePublished"
                        content={article.isoDate}
                      />
                    )}
                    {article.date}
                  </div>
                )}
              </div>
            )}
            {!!article.rating && (
              <div className={slots.metaItem()}>
                <StarIcon className={slots.metaItemIcon()} />
                {article.rating}
              </div>
            )}
          </div>
          {article.avatar && (
            <div className={slots.avatarWrap()}>
              <div className={slots.avatarImageWrapContainer()}>
                <div className={slots.avatarImageWrap()}>
                  <Image
                    className={slots.avatarImage()}
                    alt={article.title}
                    src={article.avatar}
                    fill
                  />
                </div>
                {article.doctorAvatar && (
                  <div
                    className={slots.avatarImageWrap({ doctorAvatar: true })}
                  >
                    <Image
                      className={slots.avatarImage()}
                      alt={article.title}
                      src={article.doctorAvatar}
                      fill
                    />
                  </div>
                )}
              </div>
              {article.withDoctor && (
                <div className={slots.withDoctor()}>
                  {t('article.tile.withDoctor')}
                </div>
              )}
            </div>
          )}
          <Image
            className={slots.image()}
            alt={article.title}
            src={article.image}
            width={660}
            height={480}
            {...(withMicrodata && { itemProp: 'image' })}
          />
          {isVideo && (
            <div className={slots.videoPlay()}>
              <div className={slots.videoIconWrap()}>
                <PlayIcon className={slots.videoIcon()} />
              </div>
              <div className={slots.videoText()}>{t('watch')}</div>
            </div>
          )}
          {Boolean(article?.label?.length) && (
            <div className={slots.labelContainer()}>
              {article.label?.map((label) => (
                <UiKitLabelBase
                  key={label.id}
                  className={slots.label()}
                  color="yellow"
                  data-with-hover={label.as === 'link' || !!label.onClick}
                  {...label}
                />
              ))}
            </div>
          )}
          {!article?.label?.length && article.subtitle && (
            <div className={slots.subtitleWrap()}>
              <div className={slots.subtitle()}>{article.subtitle}</div>
            </div>
          )}
          {!article.subtitle && article.tag && (
            <div className={slots.tagContainer()}>
              <UiKitLabelBase
                style={{
                  backgroundColor: article.tag.color
                }}
                className={slots.tag()}
                uppercase
              >
                {article.tag.title}
              </UiKitLabelBase>
            </div>
          )}
          {article.erid && (
            <UiAdvertisingInformerBase
              className={slots.advertisingInformer()}
              classNameContent={slots.advertisingInformerContent()}
              erid={article.erid || ''}
              advertisingWarningText={advertisingWarningText}
              sendMultiple={sendMultiple}
            />
          )}
        </div>
        <div className={slots.body()}>
          <div
            className={slots.title()}
            {...(withMicrodata && { itemProp: 'headline' })}
          >
            {article.title}
          </div>
          {article?.description && (
            <div
              className={slots.description()}
              dangerouslySetInnerHTML={{ __html: article.description }}
              {...(withMicrodata && { itemProp: 'description' })}
            />
          )}
          {!withoutButton && (
            <span className={slots.link()}>
              {t('more')}
              <span className={slots.linkIcon()}>
                <ArrowIcon />
              </span>
            </span>
          )}
        </div>
        {withMicrodata && (
          <meta
            itemProp="mainEntityOfPage"
            content={article.href}
          />
        )}
      </div>
    </div>
  );
}
